export const real = globalThis.real = {};

const supportedFeatures = {
    popover: HTMLElement.prototype.hasOwnProperty('popover'),
    css: {
        nesting: CSS.supports('selector(&)'),
        hasSelector: CSS.supports('selector(:has(*))'),
        containerQueries: CSS.supports('container-type', 'inline-size'),
        anchors: CSS.supports('anchor-name', '--foo'),
        colors: {
            oklch: CSS.supports('color', 'oklch(60% 0.15 50)'),
            mix: CSS.supports('color', 'color-mix(in srgb, red, blue)'),
            relative: CSS.supports('color', 'rgb(from red 200 g b / alpha)'),
        }
    },
    webp: {},
    touch: ('ontouchstart' in window)
};

if (supportedFeatures.touch) {
    document.documentElement.classList.add('has-touch');
}

document.documentElement.addEventListener('touchstart', () => {
    // On some hybrid devices that have both mouse and touch the window.ontouchstart is undefined, although they
    // do support touch. On such devices, the first touch event will enable touch mode.
    supportedFeatures.touch = true;
    document.documentElement.classList.add('has-touch');
}, {once: true});

// Adapted from Google's recommended way of detecting WebP support.
// https://developers.google.com/speed/webp/faq#how_can_i_detect_browser_support_for_webp
const webpTestImages = {
    lossy: "UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA",
    lossless: "UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==",
    alpha: "UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==",
    animation: "UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA"
};

const checkWebpSupport = (feature, callback) => {
    const img = new Image();
    img.onload = () => {
        callback(img.width > 0 && img.height > 0);
        img.remove();
    };
    img.onerror = () => {
        callback(false);
    };
    img.src = "data:image/webp;base64," + webpTestImages[feature];
};

Object.keys(webpTestImages).forEach(feature => {
    checkWebpSupport(feature, isSupported => {
        supportedFeatures.webp[feature] = isSupported;
    });
});

const isOldBrowser = function () {
    // Popover API is widely used in the new UI, but we don't want to use it yet as a hard requirement.
    // https://caniuse.com/mdn-api_htmlelement_popover
    //return !supportedFeatures.popover;

    // Support for container queries + :has() selector is a good middle ground for now.
    // https://caniuse.com/mdn-css_properties_container-type
    // https://caniuse.com/css-has
    return !supportedFeatures.css.containerQueries || !supportedFeatures.css.hasSelector;
};

// The very first thing we do is check if user has an obsolete browser.
if (isOldBrowser()) {
    // User is using an obsolete browser.
    if (window.location.pathname.indexOf('/error/') === -1) {
        // We are NOT on any error page. Re-direct user to obsolete browser error page.
        window.location = '/error/obsoletebrowser';
    }
}

let
    websocketConnecting = null,
    websocketOpened = null,
    tiersLoaded = {},
    tierLoadedHandlers = {},
    tiersReady = {},
    tierReadyHandlers = {}
;

document.addEventListener('mousedown', function () {
    document.body.classList.add('using-mouse');
});

document.addEventListener('keydown', function (e) {
    if (e.key) {
        document.body.classList.remove('using-mouse');
    }
});

real.TIER_1 = 'tier1';
real.TIER_2 = 'tier2';
real.TIER_ADMIN = 'admin';
real.TIER_CUSTOMER = 'customer';
real.TIER_MY_HOWSPACE = 'my-howspace';
real.TIER_WORKSPACE = 'workspace';
real.TIER_WORKSPACE_MANAGER = 'workspace-manager';

real.baseUri = document.documentElement.dataset.baseUri;
real.cdnUri = document.documentElement.dataset.cdnUri;

real.websocketConnecting = function () {
    if (window.performance && websocketConnecting === null) {
        websocketConnecting = Math.round(window.performance.now());
    }
};

real.websocketOpened = function () {
    if (window.performance && websocketOpened === null) {
        websocketOpened = Math.round(window.performance.now());
    }
};

real.tierLoaded = function (tier) {
    tiersLoaded[tier] = true;
    tierLoadedHandlers[tier]?.forEach(readyFn => readyFn());
    tierLoadedHandlers[tier] = [];
}

real.onTierLoaded = function (tier, readyFn) {
    if (tiersLoaded[tier]) {
        readyFn();
    } else {
        (tierLoadedHandlers[tier] ??= []).push(readyFn);
    }
};

real.tierReady = function (tier) {
    console.debug('TIER:', tier, 'ready in', Math.round(window.performance.now()), 'ms');
    tiersReady[tier] = true;
    tierReadyHandlers[tier]?.forEach(readyFn => readyFn());
    tierReadyHandlers[tier] = [];
};

real.onTierReady = function (tier, readyFn) {
    if (tiersReady[tier]) {
        readyFn();
    } else {
        (tierReadyHandlers[tier] ??= []).push(readyFn);
    }
};

real.loop = function (mixed, fn) {
    if (mixed instanceof NodeList || mixed instanceof HTMLCollection) {
        mixed = Array.prototype.slice.call(mixed);
    }
    if (Array.isArray(mixed)) {
        mixed.some(function (item, i) {
            // Execute the callback fn and break the loop if it returns false.
            if (fn(item, i) === false) {
                // Returning true will break the some() loop.
                // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some
                return true;
            }
        });
    } else if (typeof mixed === 'object') {
        Object.keys(mixed).some(function (key) {
            // Execute the callback fn and break the loop if it returns false.
            if (fn(mixed[key], key) === false) {
                // Returning true will break the some() loop.
                // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some
                return true;
            }
        });
    } else {
        throw new Error('real.loop can loop only arrays and objects, this does not look either of them:', mixed);
    }
};

real.loopReverse = function (mixed, fn) {
    if (mixed instanceof NodeList || mixed instanceof HTMLCollection) {
        mixed = Array.prototype.slice.call(mixed);
    }
    if (Array.isArray(mixed)) {
        for (let i = mixed.length - 1; i >= 0; i--) {
            // Execute the callback fn and break the loop if it returns false.
            if (fn(mixed[i], i) === false) {
                break;
            }
        }
    } else if (typeof mixed === 'object') {
        const keysArr = Object.keys(mixed);
        for (let i = keysArr.length - 1; i >= 0; i--) {
            // Execute the callback fn and break the loop if it returns false.
            if (fn(mixed[keysArr[i]], keysArr[i]) === false) {
                break;
            }
        }
    } else {
        throw new Error('real.loopReverse can loop only arrays and objects, this does not look either of them:', mixed);
    }
};

real.trim = function (text) {
    if (typeof text === "string") return text.trim();
    console.error("a " + typeof text + " value was passed to the trim function!");
    return "";
};

real.isOldBrowser = isOldBrowser;

real.getClipboardText = function (e) {
    let clipboardData = null;

    if (e?.clipboardData) {
        // Get clipboardData from the event.
        clipboardData = e.clipboardData;
    }

    if (e?.originalEvent?.clipboardData) {
        // Event is a jQuery event that has the native original event as a property.
        // Get clipboardData from the original event.
        clipboardData = e.originalEvent.clipboardData;
    }

    if (window.clipboardData) {
        // Get clipboardData from window.
        clipboardData = window.clipboardData;
    }

    if (clipboardData !== null) {
        // Return the clipboardData as text or return an empty string as fallback.
        return clipboardData.getData('text') || '';
    }

    // Just return an empty string as a final fallback.
    return '';
};

real.splitList = function (elem) {
    if (elem.length === 0) {
        return [];
    }
    return elem.val().split(',')
        .map(function (entry) {
            return entry.trim();
        })
        .filter(function (entry) {
            return entry !== '';
        })
    ;
};

real.copyToClipboard = async function (elem, successFn, errorFn) {
    if (typeof successFn !== 'function') {
        successFn = () => {};
    }
    if (typeof errorFn !== 'function') {
        errorFn = () => {};
    }
    if (typeof elem === 'string') {
        await navigator.clipboard.writeText(elem).then(successFn, errorFn);
    } else if (elem.is('img')) {
        let arr = elem.attr('src').split(','), mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const blob = new Blob([u8arr], {type: mime});
        const item = new ClipboardItem({"image/png": blob});
        await navigator.clipboard.write([item]).then(successFn, errorFn);
    } else if (elem.is('ul')) {
        // Copy the text of the list items, separated by newlines.
        let text = '';
        elem.find('li').each(function () {
            text += $(this).text() + '\n';
        });
        await navigator.clipboard.writeText(text).then(successFn, errorFn);
    } else if (elem.is('input, textarea')) {
        await navigator.clipboard.writeText(elem.val()).then(successFn, errorFn);
    } else {
        let cloned = elem.clone();

        // Add newlines after block-level elements
        cloned.find('br').replaceWith('\n');
        cloned.find('h1, h2, h3, h4, h5, h6, p, div').append('\n');
        cloned.find('li').prepend('- '); // optional bullets

        const text = cloned.text().trim();
        await navigator.clipboard.writeText(text).then(successFn, errorFn);
    }
};

real.deepCompare = function (a, b) {
    // Check if both objects are of the same type.
    if (typeof a !== typeof b) {
        return false;
    }

    if (typeof a === 'object' && typeof b === 'object') {
        if ((Array.isArray(a) && !Array.isArray(b)) || (!Array.isArray(a) && Array.isArray(b))) {
            // Both are "objects" but actually another one is an array and another one is an object.
            return false;
        }
    }

    // Check if both objects are null or undefined.
    if (a === null || b === null || a === undefined || b === undefined) {
        return a === b;
    }

    // Check if both objects are primitive types.
    if (typeof a !== 'object' && typeof b !== 'object') {
        return a === b;
    }

    // Check if both objects have the same number of properties.
    if (Object.keys(a).length !== Object.keys(b).length) {
        return false;
    }

    // Check if each property in a exists in b and is equal.
    for (let key in a) {
        if (!b.hasOwnProperty(key) || !real.deepCompare(a[key], b[key])) {
            return false;
        }
    }

    // If all checks passed, the objects are equal.
    return true;
};

real.isFeatureSupported = feature => {
    return supportedFeatures[feature] ?? false;
};

real.escapeHtml = (unsafeString) => {
    return unsafeString
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
};

real.logRequestStats = (method, url, execMs, queriesCount, memUsageMb, linkToAdminApp) => {
    let status = '✅';
    let logFn = console.debug;
    let hasFire = false;
    let hasWarning = false;

    let execStatus = '✅';
    if (execMs > 1000) {
        execStatus = '🔥';
        hasFire = true;
    } else if (execMs > 300) {
        execStatus = '⚠️';
        hasWarning = true;
    }

    let queriesStatus = '✅';
    if (queriesCount > 100) {
        queriesStatus = '🔥';
        hasFire = true;
    } else if (queriesCount > 50) {
        queriesStatus = '⚠️';
        hasWarning = true;
    }

    let memStatus = '✅';
    if (memUsageMb > 180) {
        memStatus = '🔥';
        hasFire = true;
    } else if (memUsageMb > 100) {
        memStatus = '⚠️';
        hasWarning = true;
    }

    if (hasFire) {
        status = '🔥';
        logFn = console.info;
    } else if (hasWarning) {
        status = '⚠️';
        logFn = console.log;
    }

    logFn(
        status + ' ' + method + ' ' + url + ' (' +
        'exec ' + execMs + ' ms ' + execStatus + ', ' +
        'queries: ' + queriesCount + ' ' + queriesStatus + ', ' +
        'mem: ' + memUsageMb + ' MB ' + memStatus + ', ' +
        'admin app: ' + linkToAdminApp + ')',
    );
};

real.onChangeVisibilityHandler = function(parent, source, target, readyFn) {
    parent.find(source).on('change', function() {
        if ($(this).prop('checked')) {
            parent.find(target + ':hidden').velocity('slideDown', 'fast', function() {
                if (readyFn) {
                    readyFn();
                }
            });
        } else {
            parent.find(target + ':visible').velocity('slideUp', 'fast');
        }
    });
};
